import { useRef, useState } from 'react';
import { FiUploadCloud, FiX, FiImage } from 'react-icons/fi';
import { useImageUpload } from '../../hooks/useImageUpload';
import toast from 'react-hot-toast';

export default function ImageUploader({ images = [], onChange }) {
  const { uploadMultiple, uploading, progress } = useImageUpload();
  const inputRef = useRef();
  const [dragging, setDragging] = useState(false);

  const handleFiles = async (files) => {
    const valid = Array.from(files).filter(f => f.type.startsWith('image/'));
    if (!valid.length) return toast.error('Please select image files only');
    if (images.length + valid.length > 5) return toast.error('Maximum 5 images per product');
    try {
      const urls = await uploadMultiple(valid);
      onChange([...images, ...urls]);
      toast.success(`${urls.length} image${urls.length > 1 ? 's' : ''} uploaded`);
    } catch {
      toast.error('Upload failed. Check your Cloudinary settings.');
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  const removeImage = (idx) => {
    onChange(images.filter((_, i) => i !== idx));
  };

  return (
    <div>
      <label className="text-xs text-gray-500 uppercase tracking-wider block mb-2">
        Product Images (max 5)
      </label>

      {/* Drop zone */}
      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
          dragging
            ? 'border-yamass-accent bg-yamass-accent/5'
            : 'border-gray-200 hover:border-yamass-accent hover:bg-gray-50'
        }`}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={e => handleFiles(e.target.files)}
        />
        {uploading ? (
          <div className="space-y-2">
            <FiUploadCloud size={28} className="mx-auto text-yamass-accent animate-bounce" />
            <p className="text-sm text-gray-500">Uploading... {progress}%</p>
            <div className="w-full bg-gray-200 rounded-full h-1.5">
              <div
                className="bg-yamass-accent h-1.5 rounded-full transition-all"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        ) : (
          <div className="space-y-1">
            <FiUploadCloud size={28} className="mx-auto text-gray-300" />
            <p className="text-sm text-gray-500">Drop images here or click to upload</p>
            <p className="text-xs text-gray-300">JPG, PNG, WebP · Max 5MB each · Up to 5 images</p>
          </div>
        )}
      </div>

      {/* Preview grid */}
      {images.length > 0 && (
        <div className="grid grid-cols-5 gap-2 mt-3">
          {images.map((url, idx) => (
            <div key={idx} className="relative group aspect-square rounded-lg overflow-hidden bg-gray-100">
              {url ? (
                <img src={url} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <FiImage size={20} className="text-gray-300" />
                </div>
              )}
              <button
                type="button"
                onClick={() => removeImage(idx)}
                className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <FiX size={10} />
              </button>
              {idx === 0 && (
                <span className="absolute bottom-1 left-1 bg-yamass-black/70 text-white text-xs px-1.5 py-0.5 rounded">
                  Main
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
